/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable @typescript-eslint/naming-convention */
// @TODO: change property names to be cammelCased
import { ICache, ICacheItem } from '@msdyn365-commerce/core-internal';
import { get, observable, set, transaction } from 'mobx';

/**
 * ClientCache implementation
 *
 * @class ClientCache
 * @implements {ICache}
 * @internal
 */
export class ClientCache implements ICache {
    private internalCache: { [type: string]: { [key: string]: ICacheItem } } = {};

    /**
     * Gets an item from the cache, and returning it wrapped in a ICacheItem
     * @param type A string representing the type of the cache item
     * @param key The key of the cache item
     */
    public Get<T>(type: string, key: string): ICacheItem<T> | null {
        if (!type) {
            throw new Error('Cannot send null arguments');
        }

        if (!key) {
            return null;
        }

        type = type.toUpperCase();
        key = key.toUpperCase();
        return this.internalCache[type] ? <ICacheItem<T>>this.internalCache[type][key] : null;
    }

    /**
     * Puts an item into the cache
     * @param type A string representing the type of the cache item
     * @param key The key of the cache item
     * @param item The item to be put into the cache
     */
    public Put<T>(type: string, key: string, item: T): ICacheItem<T> {
        if (!type || !key) {
            throw new Error('Cannot send null arguments');
        }

        type = type.toUpperCase();
        key = key.toUpperCase();
        if (this.internalCache[type] === undefined) {
            this.internalCache[type] = {};
        }
        return transaction<ICacheItem<T>>(() => {
            let cacheItem: ICacheItem = this.internalCache[type][key];
            if (cacheItem) {
                set(cacheItem, 'Item', item);
            } else {
                cacheItem = observable({ Item: item }, undefined, {
                    deep: true,
                    name: `T:${type}_K:${key}`
                });
                this.internalCache[type][key] = cacheItem;
            }

            return <ICacheItem<T>>cacheItem;
        });
    }

    /**
     * Gets the value only for a specific item from the cache
     * This differs from the Get() method as this will not return a MobX observable value
     * @param type A string representing the type of the cache item
     * @param key The key of the cache item
     */
    public GetValue<T>(type: string, key: string): T | null {
        const item = this.Get<T>(type, key);
        return item ? this.Value<T>(item) : null;
    }

    /**
     * Get the value out of a cache item
     * This returns the raw value of the item, it will *not* be a MobX observable value
     * @param item The observable cache item to retireve the value from
     */
    public Value<T>(item: ICacheItem<T>): T {
        return get(item, 'Item');
    }
}

export default ClientCache;
